import { Stack, Typography } from "@mui/material";
import type { GridColDef, GridRowParams } from "@mui/x-data-grid-premium";
import { useCallback } from "react";
import DataGrid from "@entrilia/shared/components/grid/DataGrid";
import CompletionPercentageIndicator from "../../../../common/CompletionPercentageIndicator";
import { convertISODateTime } from "@entrilia/shared/utilities/dateUtils";
import { formatDurationInSeconds } from "@entrilia/shared/utilities/formatters";
import type { DocumentViews } from "../../../../../api/types/documentActivityTypes";
import DocumentViewsGridRowDetail from "../../../investor-portal/document-activity/DocumentViewsGridRowDetail";

const isDetailPanelAvailable = (row: DocumentViews) =>
  row.pageViews != null && Object.values(row.pageViews).some((val) => val > 0);

const columnDefinitions: GridColDef<DocumentViews>[] = [
  {
    field: "userInfo",
    headerName: "Contact",
    flex: 2,
    minWidth: 160,
    renderCell: ({ row }) => (
      <Stack spacing={0.5} sx={{ width: "100%" }}>
        <Typography variant="subtitle2" noWrap>
          {row.userInfo.userName}
        </Typography>
        <Typography variant="caption" noWrap color="textSecondary">
          {row.userInfo.userEmail}
        </Typography>
      </Stack>
    ),
  },
  {
    field: "sessionStart",
    headerName: "Viewed at",
    flex: 1.5,
    minWidth: 152,
    valueFormatter: (value: string) => (value ? convertISODateTime(value) : ""),
  },
  {
    field: "totalViewDuration",
    headerName: "View Duration",
    flex: 1,
    minWidth: 110,
    headerAlign: "right",
    align: "right",
    valueFormatter: (value: number) => (value ? formatDurationInSeconds(value) : ""),
  },
  {
    field: "completionPercentage",
    headerName: "View Completion",
    type: "number",
    flex: 1,
    minWidth: 130,
    headerAlign: "center",
    align: "center",
    renderCell: ({ row }) => {
      if (!row.completionPercentage) {
        return null;
      }

      return <CompletionPercentageIndicator value={row.completionPercentage} />;
    },
  },
  {
    field: "lastDownloadTime",
    headerName: "Downloaded at",
    flex: 1.5,
    minWidth: 152,
    valueFormatter: (value: string | undefined) => convertISODateTime(value),
  },
];

interface Props {
  documentViews: DocumentViews[];
  loading: boolean;
  excludeColumns?: string[];
}

const UserDocumentActivitySection = ({ documentViews, loading, excludeColumns }: Props) => {
  const columnVisibilityModel = Object.fromEntries((excludeColumns ?? []).map((fieldId) => [fieldId, false]));

  const getDetailPanelContent = useCallback(
    ({ row }: GridRowParams<DocumentViews>) =>
      isDetailPanelAvailable(row) ? <DocumentViewsGridRowDetail row={row} /> : undefined,
    []
  );

  const getDetailPanelHeight = useCallback(() => 256, []);

  return (
    <Stack spacing={2} sx={{ height: "100%" }}>
      <Typography variant="subtitle2" sx={{ pt: 1 }}>
        All Activity
      </Typography>
      <DataGrid<DocumentViews>
        rows={documentViews}
        columns={columnDefinitions}
        multilineRows
        loading={loading}
        getDetailPanelContent={getDetailPanelContent}
        getDetailPanelHeight={getDetailPanelHeight}
        noRowsText="No document activity"
        columnVisibilityModel={columnVisibilityModel}
        disableColumnSorting
        disableColumnReorder
      />
    </Stack>
  );
};

export default UserDocumentActivitySection;
